import PropTypes from "prop-types";
import React, { useState, useCallback, useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  Select,
  MenuItem,
  Typography,
  TextField,
  Grid,
  Button,
  Box,
  Divider,
  CircularProgress
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DualList from "../../../../components/DualList/DualList";
import ProcessingModal from "../../../../components/ProcessingModal/ProcessingModal";
import { connect } from "react-redux";
import { enterKeyCode, states } from "../../../../constants";
import { useTranslation } from "react-i18next";
import {
  fetchData,
  markJobFinished,
  updateAssignments
} from "../../../../redux/actions/adminConsole/content/assignmentsActions";
import { ActionCableConsumer } from "react-actioncable-provider";

const defaultFilters = {
  search: "",
  state: { value: "all", label: "courseAssignments.stateFilterAllOption" }
};

export const listTypes = {
  none: { value: "none", label: "courseAssignments.selectTypeNoneOption" },
  location: {
    value: "location",
    label: "courseAssignments.selectTypeLocationOption"
  },
  position: {
    value: "position",
    label: "courseAssignments.selectTypePositionOption"
  },
  group: { value: "group", label: "courseAssignments.selectTypeGroupOption" }
};

const stateFilterOptions = [defaultFilters.state].concat(states);

const useStyles = makeStyles(theme => ({
  label: {
    marginRight: theme.spacing(3),
    color: theme.palette.text.secondary
  },
  selectType: {
    ...theme.components.MuiTypography.styleOverrides.h5,
    color: theme.palette.primary.main
  },
  filterButton: {
    marginLeft: theme.spacing(3)
  }
}));

const getListItemDetails = (type, item) => {
  return {
    id: item.id,
    name: item.location ? item.location.name : item.name,
    subtitles:
      type === listTypes.location.value && item.location
        ? [
            item.location.address1,
            item.location.address2,
            `${item.location.city}, ${item.location.state} ${item.location.zip}`
          ]
        : []
  };
};

const filterItemList = (list, filterFunc) => {
  return list.filter(item => filterFunc(item));
};

const CourseAssignments = ({
  assignmentLists,
  fetchData,
  updateAssignments,
  markJobFinished,
  runningJobId,
  loading,
  location: { pathname }
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const courseId = pathname.match(/[0-9]+/g).pop();
  const [type, setType] = useState(listTypes.none.value);
  const [search, setSearch] = useState(defaultFilters.search);
  const [state, setState] = useState(defaultFilters.state);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);

  const [lists, setLists] = useState(
    assignmentLists
      ? assignmentLists
      : {
          assigned: [],
          unassigned: []
        }
  );

  const [unselectedList, setUnselectedList] = useState([]);
  const [selectedList, setSelectedList] = useState([]);

  const fetchReferenceObjects = () => {
    fetchData(courseId, type, list => {
      setSelectedList(filterItemList(list.assigned, filterFunc));
      setUnselectedList(filterItemList(list.unassigned, filterFunc));
    });
  };

  useEffect(() => {
    if (assignmentLists !== lists) {
      setLists(assignmentLists);
    }
  }, [assignmentLists]);

  useEffect(() => {
    clearAll();
    if (type !== listTypes.none.value) {
      fetchReferenceObjects();
    }
  }, [type]);

  useEffect(() => {
    if (type !== listTypes.none.value) {
      setSelectedList(filterItemList(lists.assigned, filterFunc));
      setUnselectedList(filterItemList(lists.unassigned, filterFunc));
    }
  }, [lists, appliedFilters]);

  const clearAll = () => {
    setSearch(defaultFilters.search);
    setState(defaultFilters.state);
    setAppliedFilters(defaultFilters);
  };

  /**
   * Filters the items in the Unassigned column by the search input provided the user.
   * If the selected type is Location, also filters by the selected state.
   */
  const filterFunc = useCallback(
    item => {
      const { search, state } = appliedFilters;
      const containsSearchValue =
        !search || (item.name && item.name.toLowerCase().indexOf(search) >= 0);
      const locationWithinSelectedState =
        type === listTypes.location.value
          ? state.value === defaultFilters.state.value ||
            item.location.state === state.value
          : true;
      const groupWithinSelectedState =
        type === listTypes.group.value
          ? state.value === defaultFilters.state.value ||
            item.groupLocations.indexOf(state.value) >= 0
          : true;
      return (
        containsSearchValue &&
        locationWithinSelectedState &&
        groupWithinSelectedState
      );
    },
    [type, appliedFilters]
  );

  /**
   * Called when the user clicks on the Filter List button
   */
  const handleFilter = () => {
    setAppliedFilters({
      search: search.toLowerCase(),
      state
    });
  };

  /**
   * Marks all courses as active except for the ones which are filtered out
   */
  const addAll = () => {
    setLists({
      assigned: [...lists.assigned, ...unselectedList],
      unassigned: lists.unassigned.filter(
        unfilteredItem =>
          !unselectedList.some(
            filteredItem => filteredItem.id === unfilteredItem.id
          )
      )
    });
  };

  /**
   * Adds all courses (except for the filtered out) to the unselected list
   */
  const removeAll = () => {
    setLists({
      ...lists,
      unassigned: [...selectedList, ...lists.unassigned],
      assigned: lists.assigned.filter(
        unfilteredItem =>
          !selectedList.some(
            filteredItem => filteredItem.id === unfilteredItem.id
          )
      )
    });
  };

  const unassignItem = selectedItem => {
    const item = lists.assigned.find(
      listItem => listItem.id === selectedItem.id
    );
    setLists({
      ...lists,
      assigned: lists.assigned.filter(
        listItem => listItem.id !== selectedItem.id
      ),
      unassigned: [...lists.unassigned, item]
    });
  };

  const assignItem = selectedItem => {
    const item = lists.unassigned.find(
      listItem => listItem.id === selectedItem.id
    );
    setLists({
      ...lists,
      assigned: [...lists.assigned, item],
      unassigned: lists.unassigned.filter(
        listItem => listItem.id !== selectedItem.id
      )
    });
  };

  const handleSaveAssignments = () => {
    const idsToAdd = [];
    const idsToRemove = [];
    assignmentLists.assigned.forEach(initialItem => {
      if (lists.unassigned.some(item => item.id === initialItem.id)) {
        idsToRemove.push(initialItem.id);
      }
    });
    assignmentLists.unassigned.forEach(initialItem => {
      if (lists.assigned.some(item => item.id === initialItem.id)) {
        idsToAdd.push(initialItem.id);
      }
    });
    updateAssignments(courseId, type, idsToAdd, idsToRemove);
  };

  return (
    <div>
      {!!runningJobId && (
        <ActionCableConsumer
          channel={{
            channel: "CheckAssignmentEngineChannel",
            job_id: runningJobId
          }}
          onReceived={response => {
            markJobFinished();
            fetchReferenceObjects();
          }}
        />
      )}
      <ProcessingModal
        open={!!runningJobId}
        messageText={t("courseAssignments.processingMessage")}
      />
      <Box mb={1.75} mt={2} display="flex" alignItems="center">
        <Typography className={classes.label} variant="h5" sx={{ mt: -0.25 }}>
          {t("courseAssignments.selectTypeLabel")}
        </Typography>
        <Select
          className={classes.selectType}
          value={type}
          onChange={ev => {
            setType(ev.target.value);
          }}
          disableUnderline
          renderValue={value => t(listTypes[value].label)}
          data-qa={`select-${type}`}
        >
          {Object.values(listTypes).map(type => (
            <MenuItem key={type.value} value={type.value}>
              {t(type.label)}
            </MenuItem>
          ))}
        </Select>
        {loading && <CircularProgress size={30} sx={{ ml: 3 }} />}
      </Box>
      <Grid container spacing={3} className={classes.block}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            value={search}
            onChange={ev => {
              setSearch(ev.target.value);
            }}
            InputProps={{
              endAdornment: <SearchIcon color="action" />,
              placeholder: t("courseAssignments.searchPlaceholder"),
              disableUnderline: true,
              onKeyDown: event =>
                event.keyCode === enterKeyCode && handleFilter() // filters when enter is pressed
            }}
            fullWidth
            variant="filled"
            data-qa="search-field"
            hiddenLabel
          />
        </Grid>
        {(type === "location" || type === "group") && (
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              value={state}
              onChange={ev => {
                setState(ev.target.value);
              }}
              InputProps={{
                disableUnderline: true
              }}
              SelectProps={{
                renderValue: val =>
                  `${t("courseAssignments.stateLabel")} ${t(val.label)}`,
                "data-qa": state.label
              }}
              fullWidth
              variant="filled"
              hiddenLabel
            >
              {stateFilterOptions.map(state => (
                <MenuItem key={state.value} value={state}>
                  {t(state.label)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <Box display="flex" alignItems="center" height="100%">
            <Button color="primary" onClick={clearAll}>
              {t("courseAssignments.clearAllBtn")}
            </Button>
            <Button
              className={classes.filterButton}
              color="primary"
              variant="contained"
              onClick={handleFilter}
            >
              {t("courseAssignments.filterListBtn")}
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Box my={4}>
        <Divider />
      </Box>
      {type !== listTypes.none.value && (
        <Box mb={4}>
          <DualList
            onAddAll={addAll}
            onRemoveAll={removeAll}
            onAddToSelectedList={assignItem}
            onAddToUnselectedList={unassignItem}
            selectedListHeader={`${t("courseAssignments.assignedHeader")} ${t(
              listTypes[type].label + "Plural"
            )}`}
            unselectedListHeader={`${t(
              "courseAssignments.unassignedHeader"
            )} ${t(listTypes[type].label + "Plural")}`}
            selectedList={selectedList.map(item =>
              getListItemDetails(type, item)
            )}
            unselectedList={unselectedList.map(item =>
              getListItemDetails(type, item)
            )}
          />
        </Box>
      )}
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveAssignments}
        >
          {t("courseAssignments.saveAssignmentsBtn")}
        </Button>
      </Box>
    </div>
  );
};

CourseAssignments.propTypes = {
  assignmentLists: PropTypes.shape({
    assigned: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        name: PropTypes.string,
        location: PropTypes.shape({
          name: PropTypes.string,
          address1: PropTypes.string,
          address2: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
          zip: PropTypes.string
        }),
        groupLocations: PropTypes.arrayOf(PropTypes.string)
      })
    ),
    unassigned: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        name: PropTypes.string,
        location: PropTypes.shape({
          name: PropTypes.string,
          address1: PropTypes.string,
          address2: PropTypes.string,
          city: PropTypes.string,
          state: PropTypes.string,
          zip: PropTypes.string
        }),
        groupLocations: PropTypes.arrayOf(PropTypes.string)
      })
    )
  }),
  runningJobId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
  fetchData: PropTypes.func,
  updateAssignments: PropTypes.func
};

const mapStateToProps = state => {
  return {
    assignmentLists: state.acContentAssignmentsState.referenceObjects,
    runningJobId: state.acContentAssignmentsState.jobId,
    loading: state.acContentAssignmentsState.loading
  };
};

export default connect(mapStateToProps, {
  fetchData,
  updateAssignments,
  markJobFinished
})(CourseAssignments);
